import { sessionApiUrl } from '../realtime/sessionEndpoint.js';
import { readBoundedArrayBuffer } from '../../cloudflare/requestBody.js';

// Independent recovery implementation. It deliberately owns a new namespace:
// the denied original's storage and backup formats have not been verified.
const STORAGE_NAME = 'qline-appyland-recovery:private-library-access-v1';
const BACKUP_KIND = 'qline-appyland-recovery-private-library-access';
const validKey = value => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const storageFor = value => value || globalThis.localStorage;
const validString = (value, maximum) => typeof value === 'string' && value.length > 0 && value.length <= maximum;
function validateResponse(action, value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('The library response was invalid.');
  if (action === 'list') {
    if (!Array.isArray(value.entries) || value.entries.length > 100 || value.entries.some(entry =>
      !entry || typeof entry !== 'object' || Array.isArray(entry) || !validString(entry.id, 160) || !validString(entry.title, 100)
      || ['themes', 'ageGuide', 'contentNotes', 'ratingSource'].some(field => entry[field] !== undefined && typeof entry[field] !== 'string')))
      throw new Error('The library entry list was invalid. Retry.');
    if (value.limits !== undefined && (!value.limits || typeof value.limits !== 'object' || Array.isArray(value.limits)
      || !['entries', 'bytes', 'objects'].every(field => Number.isSafeInteger(value.limits[field]) && value.limits[field] >= 0)
      || !Number.isSafeInteger(value.retainedBytes) || value.retainedBytes < 0))
      throw new Error('The library capacity response was invalid. Retry.');
  }
  if (action === 'open') {
    const scenario = value.scenario;
    if (!scenario || !validString(scenario.id, 160) || !validString(scenario.title, 100)
      || !Array.isArray(scenario.characters) || !Array.isArray(scenario.scenes) || !Array.isArray(scenario.cues)
      || scenario.characters.length > 16 || scenario.scenes.length > 30 || scenario.cues.length > 1000
      || scenario.cues.some(cue => !cue || typeof cue !== 'object'
        || ['text', 'dialogueText', 'directionText', 'spokenDirectionText'].some(field => cue[field] !== undefined && typeof cue[field] !== 'string'))
      || (scenario.originalScript !== undefined && typeof scenario.originalScript !== 'string'))
      throw new Error('The saved scenario response was invalid. Retry.');
  }
  return value;
}

function persist(storage, value) {
  const encoded = JSON.stringify({ version: 1, key: value });
  storage.setItem(STORAGE_NAME, encoded);
  if (storage.getItem(STORAGE_NAME) !== encoded) throw new Error('Library access was not saved. Keep this browser open and retry.');
}
export function libraryKey(create = false, storage) {
  const target = storageFor(storage);
  const raw = target.getItem(STORAGE_NAME);
  if (raw !== null) {
    let saved;
    try { saved = JSON.parse(raw); } catch { throw new Error('Damaged library access was not replaced. Preserve and restore its backup.'); }
    if (saved?.version !== 1 || !validKey(saved.key))
      throw new Error('Damaged library access was not replaced. Preserve and restore its backup.');
    return saved.key;
  }
  if (!create) return null;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  persist(target, value);
  return value;
}
export function libraryAccessBackup(storage) {
  const value = libraryKey(false, storage);
  if (!value) throw new Error('No recovery library access is saved on this browser.');
  return JSON.stringify({ kind: BACKUP_KIND, version: 1, key: value });
}
export function restoreLibraryAccess(input, storage) {
  if (typeof input !== 'string' || input.length > 4096) throw new Error('Choose a small recovery library access backup.');
  let saved;
  try { saved = JSON.parse(input); } catch { throw new Error('The library access backup is not valid JSON.'); }
  if (!saved || Object.keys(saved).sort().join(',') !== 'key,kind,version'
    || saved.kind !== BACKUP_KIND || saved.version !== 1 || !validKey(saved.key))
    throw new Error('This is not a supported recovery library access backup. The existing access was preserved.');
  persist(storageFor(storage), saved.key);
  return saved.key;
}
export async function libraryRequest(action, input = {}, options = {}) {
  if (!['list', 'open', 'remove', 'use', 'save', 'load'].includes(action)) throw new Error('Unsupported public library action.');
  const scope = input.scope ?? (action === 'remove' ? 'private' : 'catalog');
  if (!['catalog', 'private'].includes(scope)) throw new Error('Choose a valid library scope.');
  if (action === 'remove' && scope !== 'private') throw new Error('Shared catalog removal is not available.');
  if (scope === 'private' && !validKey(options.key)) throw new Error('Private library access is required.');
  if (!['list', 'save'].includes(action) && (typeof input.id !== 'string' || !input.id || input.id.length > 160))
    throw new Error('Choose a valid saved edition.');
  const signal = options.signal ? AbortSignal.any([options.signal, AbortSignal.timeout(30000)]) : AbortSignal.timeout(30000);
  signal.throwIfAborted();
  const roomAction = ['use', 'save', 'load'].includes(action);
  const room = options.room;
  if (roomAction && (!/^[A-Z0-9]{6}$/.test(room?.roomCode || '') || room?.role !== 'host' || typeof room?.token !== 'string' || !room.token))
    throw new Error('Authenticated Director access is required.');
  const headers = { 'content-type': 'application/json' };
  if (roomAction || scope === 'private') headers.authorization = 'Bearer ' + (roomAction ? room.token : options.key);
  const body = { scope, ...(action === 'list' ? {} : { id: input.id }) };
  if (roomAction && scope === 'private') body.key = options.key;
  const response = await (options.fetchImpl || globalThis.fetch)(sessionApiUrl(roomAction ? '/api/rooms/' + room.roomCode + '/prepared-library/' + action : '/api/prepared-library/' + action), {
    method: 'POST', headers, body: JSON.stringify(body),
    signal, cache: 'no-store', credentials: 'omit', redirect: 'error',
  });
  const bytes = await readBoundedArrayBuffer(response, 4 * 1024 * 1024, 'Saved library response is too large.', signal);
  let result;
  try { result = JSON.parse(new TextDecoder().decode(bytes)); } catch { throw new Error('The library response could not be read. Retry.'); }
  if (!response.ok) throw new Error(typeof result?.message === 'string' ? result.message : 'The library request failed. Retry.');
  return validateResponse(action, result);
}
